import { getHandwriting, putHandwriting } from '../lib/handwriting.ts';
import { useStore } from '../store/store.ts';
import { COLLECTION_NAMES, type CollectionName } from '../store/types.ts';
import { isSyncConfigured, supabase } from '../auth/supabase.ts';
import { latestTimestamp, mergeCollection, pendingRows, purgeTombstones, type Row } from './merge.ts';
import {
  SETTINGS_FIELDS,
  SETTINGS_ROW_ID,
  SETTINGS_TABLE,
  TABLES,
  toIsoString,
  toLocal,
  toRemote,
} from './schema.ts';
import { isSeedRow } from '../store/defaults.ts';
import { setSyncStatus } from './status.ts';
import { formatSyncError } from './error.ts';

/**
 * Pull cursor per table, kept out of the synced payload.
 *
 * The `_v2` suffix is a deliberate one-off reset. Cursors written by the
 * previous version could sit past rows that never reached the store — a pull
 * capped at `max-rows`, or a device whose first sync pushed instead of pulling
 * — and a cursor only ever moves forward, so those rows could not be asked for
 * again. Changing the key makes every install re-pull its account once, which
 * is idempotent: the merge is last-write-wins and local dirty rows still push.
 */
const CURSOR_KEY = 'jdc_anso_sync_cursors_v2';
const LEGACY_CURSOR_KEY = 'jdc_anso_sync_cursors';
const EPOCH = '1970-01-01T00:00:00.000Z';

type Cursors = Record<string, string>;

function readCursors(): Cursors {
  localStorage.removeItem(LEGACY_CURSOR_KEY);
  try {
    return JSON.parse(localStorage.getItem(CURSOR_KEY) ?? '{}') as Cursors;
  } catch {
    return {};
  }
}

function writeCursors(cursors: Cursors): void {
  localStorage.setItem(CURSOR_KEY, JSON.stringify(cursors));
}

/** Cursors are per-account: switching user must not reuse the previous one. */
function cursorKey(userId: string, table: string): string {
  return `${userId}:${table}`;
}

export function resetCursors(): void {
  localStorage.removeItem(CURSOR_KEY);
  localStorage.removeItem(LEGACY_CURSOR_KEY);
}

// --------------------------------------------------------------- handwriting

function handwritingPath(userId: string, observationId: string): string {
  return `${userId}/${observationId}.png`;
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  return await (await fetch(dataUrl)).blob();
}

/**
 * Upload any stylus note that exists locally but not yet remotely.
 *
 * Done before the observation rows are pushed, so a row never advertises a
 * `handwritingPath` the other device cannot fetch.
 */
async function pushHandwriting(userId: string): Promise<void> {
  const client = supabase;
  if (!client) return;

  const observations = useStore
    .getState()
    .observations.filter((o) => o.dirty && o.hasHandwriting && !o.handwritingPath);

  for (const observation of observations) {
    const dataUrl = await getHandwriting(observation.id);
    if (!dataUrl) continue;

    const path = handwritingPath(userId, observation.id);
    const { error } = await client.storage
      .from('handwriting')
      .upload(path, await dataUrlToBlob(dataUrl), { contentType: 'image/png', upsert: true });

    if (error) throw error;

    useStore.setState((s) => ({
      observations: s.observations.map((o) =>
        o.id === observation.id ? { ...o, handwritingPath: path } : o,
      ),
    }));
  }
}

/**
 * Fetch a stylus note that arrived from the other device.
 *
 * Called lazily by the UI rather than during sync: these are the heaviest
 * objects in the dataset and most are never looked at again.
 */
export async function ensureHandwritingLocally(observationId: string): Promise<string | null> {
  const local = await getHandwriting(observationId);
  if (local) return local;

  const client = supabase;
  if (!client) return null;

  const observation = useStore.getState().observations.find((o) => o.id === observationId);
  if (!observation?.handwritingPath) return null;

  const { data, error } = await client.storage
    .from('handwriting')
    .download(observation.handwritingPath);
  if (error || !data) return null;

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(data);
  });

  await putHandwriting(observationId, dataUrl);
  return dataUrl;
}

// ------------------------------------------------------------------- paging

/**
 * Rows per pull request.
 *
 * A plain `select()` is capped by PostgREST's `max-rows` (1000 on a default
 * Supabase project). Silently returning the first thousand rows would be bad
 * enough on its own, but the cursor is then advanced to the newest row of that
 * page — so everything the cap hid is older than the cursor and is never asked
 * for again. A device restoring a full school year would keep a green badge
 * over a half-empty journal. Hence: an explicit order, an explicit page size,
 * and a loop.
 */
const PAGE_SIZE = 500;

async function pullPages(table: string, since: string): Promise<Record<string, unknown>[]> {
  const client = supabase!;
  const rows: Record<string, unknown>[] = [];

  for (let offset = 0; ; offset += PAGE_SIZE) {
    const { data, error } = await client
      .from(table)
      .select('*')
      .gt('updated_at', since)
      // A whole upsert shares one transaction timestamp, so `updated_at` alone
      // is not a total order and pages would overlap or skip without `id`.
      .order('updated_at', { ascending: true })
      .order('id', { ascending: true })
      .range(offset, offset + PAGE_SIZE - 1);

    if (error) throw error;

    const page = (data ?? []) as Record<string, unknown>[];
    rows.push(...page);
    if (page.length < PAGE_SIZE) return rows;
  }
}

// ---------------------------------------------------------------- one table

async function pushCollection(name: CollectionName): Promise<void> {
  const client = supabase!;
  const spec = TABLES[name];

  const local = useStore.getState()[name] as unknown as Row[];
  const dirty = pendingRows(local);
  if (!dirty.length) return;

  const payload = dirty.map((r) => toRemote(spec.fields, r as unknown as Record<string, unknown>));
  const { data, error } = await client
    .from(spec.table)
    .upsert(payload, { onConflict: 'user_id,id' })
    .select();

  if (error) throw error;

  // Adopt the server's updated_at so both devices compare the same clock.
  const accepted = new Map((data ?? []).map((r: Record<string, unknown>) => [r['id'] as string, r]));
  useStore.setState((s) => ({
    [name]: (s[name] as unknown as Row[]).map((row) => {
      const server = accepted.get(row.id);
      if (!server) return row;
      return { ...row, updatedAt: toIsoString(server['updated_at']) ?? row.updatedAt, dirty: false };
    }),
  }) as never);
}

async function pullCollection(name: CollectionName, userId: string, cursors: Cursors): Promise<number> {
  const spec = TABLES[name];
  const key = cursorKey(userId, spec.table);
  const since = cursors[key] ?? EPOCH;

  const remoteRows = await pullPages(spec.table, since);
  const incoming = remoteRows.map(
    (r) => toLocal(spec.fields, r) as unknown as Row,
  );

  const current = useStore.getState()[name] as unknown as Row[];
  const merged = mergeCollection(current, incoming);

  useStore.setState({ [name]: purgeTombstones(merged.rows) } as never);
  cursors[key] = latestTimestamp(incoming, since);

  return incoming.length;
}

// -------------------------------------------------------------- settings row

async function pushSettings(): Promise<void> {
  const client = supabase!;
  const local = useStore.getState().settings;
  if (!local.dirty) return;

  const { data, error } = await client
    .from(SETTINGS_TABLE)
    .upsert(
      toRemote(SETTINGS_FIELDS, {
        ...(local as unknown as Record<string, unknown>),
        id: SETTINGS_ROW_ID,
      }),
      { onConflict: 'user_id,id' },
    )
    .select()
    .single();

  if (error) throw error;
  useStore.setState((s) => ({
    settings: {
      ...s.settings,
      updatedAt: toIsoString(data['updated_at']) ?? s.settings.updatedAt,
      dirty: false,
    },
  }));
}

async function pullSettings(userId: string, cursors: Cursors): Promise<number> {
  const key = cursorKey(userId, SETTINGS_TABLE);
  const since = cursors[key] ?? EPOCH;

  // Not `maybeSingle()`: the account is supposed to hold exactly one settings
  // row, but if it ever holds two that would fail the whole sync rather than
  // just picking the newer one.
  const rows = await pullPages(SETTINGS_TABLE, since);
  const remote = rows[rows.length - 1];
  if (!remote) return 0;

  const incoming = toLocal(SETTINGS_FIELDS, remote);
  const incomingAt = incoming['updatedAt'] as string;
  const currentSettings = useStore.getState().settings;

  if (incomingAt > currentSettings.updatedAt) {
    useStore.setState({
      settings: {
        teacher: incoming['teacher'] as string,
        className: incoming['className'] as string,
        schoolYear: incoming['schoolYear'] as string,
        updatedAt: incomingAt,
        deletedAt: null,
        dirty: false,
      },
    });
  }

  cursors[key] = incomingAt || since;
  return 1;
}

// ------------------------------------------------------- first sync on a device

/** True when no table has ever been pulled for this account on this device. */
function isFirstSyncForAccount(cursors: Cursors, userId: string): boolean {
  return !Object.keys(cursors).some((key) => key.startsWith(`${userId}:`));
}

/**
 * Adopt the account before offering anything to it.
 *
 * A freshly installed device starts on `createDefaults()`: the demo lesson on
 * the first Monday, the sample task, the sticky note, and `teacher: 'Anso'`.
 * Pushing first would sprinkle those into an account that already holds a
 * school year of real work. So the first sync after signing in pulls
 * everything, then drops whichever seeds are still untouched — but only if the
 * account had something to replace them with, or a genuinely new account would
 * open on a blank week.
 */
async function adoptAccount(userId: string, cursors: Cursors): Promise<void> {
  let pulled = await pullSettings(userId, cursors);
  for (const name of COLLECTION_NAMES) {
    pulled += await pullCollection(name, userId, cursors);
  }

  if (pulled === 0) return;

  useStore.setState((s) => {
    const patch: Record<string, unknown> = {};
    for (const name of COLLECTION_NAMES) {
      const rows = s[name] as unknown as (Row & Record<string, unknown>)[];
      const kept = rows.filter((row) => !isSeedRow(row));
      if (kept.length !== rows.length) patch[name] = kept;
    }
    return patch as never;
  });
}

// ------------------------------------------------------------------ driver

function countPending(): number {
  const state = useStore.getState();
  let n = state.settings.dirty ? 1 : 0;
  for (const name of COLLECTION_NAMES) {
    n += (state[name] as unknown as Row[]).filter((r) => r.dirty).length;
  }
  return n;
}

let running = false;
let queued = false;

/**
 * Push local changes, then pull remote ones, for every collection.
 *
 * Serialised: a second call while one is in flight sets a flag and runs once
 * afterwards, rather than interleaving two passes over the same tables.
 */
export async function syncNow(): Promise<void> {
  if (!isSyncConfigured || !supabase) {
    setSyncStatus({ phase: 'disabled', pending: 0 });
    return;
  }

  if (running) {
    queued = true;
    return;
  }

  // getSession reads the stored session and refreshes it only when it has
  // actually expired. getUser, which this used to call, always went to
  // /auth/v1/user — one network round trip in front of every sync, and the
  // source of the "JWT expired" failure a phone reports when it wakes up with
  // a stale access token: the call fails, and the app reports itself signed
  // out rather than simply retrying.
  const { data: auth } = await supabase.auth.getSession();
  const userId = auth.session?.user?.id;
  if (!userId) {
    setSyncStatus({ phase: 'signed-out', pending: countPending() });
    return;
  }

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    setSyncStatus({ phase: 'offline', pending: countPending() });
    return;
  }

  running = true;
  setSyncStatus({ phase: 'syncing', lastError: null });

  try {
    const cursors = readCursors();

    // A device that has never pulled this account adopts it first. Cursors are
    // keyed by user id and only written after a fully successful pass, so this
    // is true exactly once per account per install — and stays true while the
    // first attempt keeps failing.
    if (isFirstSyncForAccount(cursors, userId)) {
      await adoptAccount(userId, cursors);
    }

    await pushHandwriting(userId);

    await pushSettings();
    await pullSettings(userId, cursors);

    for (const name of COLLECTION_NAMES) {
      await pushCollection(name);
      await pullCollection(name, userId, cursors);
    }

    writeCursors(cursors);
    setSyncStatus({
      phase: 'idle',
      pending: countPending(),
      lastSyncAt: new Date().toISOString(),
      lastError: null,
    });
  } catch (error) {
    // Never destructive: a failed sync leaves local data and its dirty flags
    // exactly as they were, so the next attempt retries the same changes.
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    setSyncStatus({
      phase: offline ? 'offline' : 'error',
      pending: countPending(),
      lastError: formatSyncError(error),
    });
  } finally {
    running = false;
    if (queued) {
      queued = false;
      void syncNow();
    }
  }
}

/**
 * Forget every cursor and pull the whole account again.
 *
 * The escape hatch for a device that shows a green badge over a journal that
 * is missing rows — which used to be unrecoverable, since a cursor only moves
 * forward. Non-destructive: pending local changes stay dirty and still push.
 */
export async function resyncFromScratch(): Promise<void> {
  resetCursors();
  await syncNow();
}

/** Debounced trigger for "something changed, sync soon". */
let debounce: ReturnType<typeof setTimeout> | undefined;

export function scheduleSync(delayMs = 2000): void {
  clearTimeout(debounce);
  debounce = setTimeout(() => void syncNow(), delayMs);
}

let started = false;

/**
 * Wire the triggers: startup, reconnection, tab focus, any local mutation,
 * and a slow interval as a backstop for all of the above failing.
 */
export function startSync(): () => void {
  if (started || !isSyncConfigured) return () => undefined;
  started = true;

  const onOnline = () => void syncNow();
  const onVisible = () => {
    if (document.visibilityState === 'visible') scheduleSync(200);
  };

  window.addEventListener('online', onOnline);
  window.addEventListener('offline', () =>
    setSyncStatus({ phase: 'offline', pending: countPending() }),
  );
  document.addEventListener('visibilitychange', onVisible);

  const unsubscribe = useStore.subscribe(() => {
    setSyncStatus({ pending: countPending() });
    scheduleSync();
  });

  const interval = setInterval(() => void syncNow(), 5 * 60 * 1000);

  void syncNow();

  return () => {
    started = false;
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisible);
    unsubscribe();
    clearInterval(interval);
    clearTimeout(debounce);
  };
}
